// Tooltips: the name of every control that has no words of its own
// (docs/tooltips-plan.md D721–D731).
//
// An icon button says what it does with a drawing, and a drawing is a guess
// until the pointer rests on it or the keyboard lands on it. Then this file
// says it in words — one or two, on one line, in a small card beside the
// control. The words are the control's own accessible name, so the markup
// carries them once and both kinds of reader get the same thing:
//
//     <button data-tip="Закрыть" aria-label="Закрыть">…</button>       a control
//     <span role="img" data-tip="Изменено" aria-label="Изменено">…</span>  a mark
//
// and these markers for the exceptions:
//
//     data-tip-side="right"  the first place to try; above is the default
//     data-tip-name          the words are a name — a person's, a pack's, a
//                            tone's — not a hint, and the audit lets them run
//                            past two words
//     data-tip-sentence      the tip says what a price buys — «Повысить
//                            качество стрима до 1440p 60 fps на 30 дней»
//                            (docs/floats-plan.md D992): a sentence, which
//                            the audit lets run past two words
//     data-tip-none          no tip over this element or inside it: it has a
//                            hover card of its own (`FaceStack`)
//     data-tip-label         on a child: the control's own words, which the
//                            layout may hide; no tip while they are on screen
//     data-tip-keys          the keys that do what the control does —
//                            «Ctrl+Shift+M», «Enter» — drawn as keycaps after
//                            the words (D742); a control a chord of the static
//                            map presses (`data-hotkey`, D747) gets that chord
//                            without it
//
// Every window draws its tips into its own mount — one empty element its root
// renders and nothing but this file writes into (D729):
//
//     <div class="echo-tips" aria-hidden="true"></div>
//
// When (D725): after 500 ms at rest under a mouse or a pen — at once if another
// tip went down less than 300 ms ago — and at once when the keyboard's focus
// lands with `:focus-visible`. Gone at once when the pointer or the focus
// leaves, on any press, on any key that is not a bare modifier, on a wheel, on
// a scroll of whatever holds the control, when the window loses focus or is
// hidden, and when the control leaves the document. Nothing here stops or
// prevents an event: Escape takes the tip down and goes on to the window's own
// Escape stack (D726). Where (D727): `window.__echoPlace` (`placement.js`,
// D425), above and centred first.
//
// Everything is delegated from `document`: a framework renders the attribute
// and has nothing else to do — no handler per control, no state, and on the
// desktop no round trip to Rust per pointer move. The tips are a pool drawn by
// index (D731); this file only ever asks for the first. The Ctrl mode draws
// its own, all at once, into a box of its own in the same mount (`keytips.js`,
// D736–D746), and while it is up no tip of this file's comes up beside it.
//
//     window.__echoTips.hide()       takes down whatever is up
//     window.__echoTips.show(el)     el's tip at once, as the keyboard's focus
//                                    raises one — for the arrows' mark, which
//                                    leaves the focus where it was (D763)
//     window.__echoTips.words(el)    what a control's tip says now — nothing
//                                    while its own words are on screen
//     window.__echoTips.keycaps(el, keys)  appends «Ctrl+Shift+M» to el as one
//                                    keycap a key (D742)
//     window.__echoTips.audit(root)  what a migration missed: a control with no
//                                    words and no tip, a control with no name, a
//                                    tip that is not the name, a `title` left
//                                    behind, a control's tip past two words —
//                                    as [{el, why}]
//
// Shared by both clients for the reason `placement.js` is: one behaviour, on
// one markup, whichever window draws it.
(function () {
  "use strict";

  if (typeof window === "undefined" || window.__echoTips) return;

  var MOUNT = ".echo-tips";
  var TIP = "[data-tip]";
  var NONE = "[data-tip-none]";
  var LABEL = "[data-tip-label]";
  // D725: what a Windows tooltip waits for, and how long after one went down
  // the next one comes without waiting — a row of icons reads one by one.
  var DELAY = 500;
  var WARM = 300;
  // D727: how far off the control, and off the window's edge (D425's gap).
  var GAP = 6;
  var EDGE = 8;
  // The keys after which a tip still means something: the bare modifiers,
  // which are also what the Ctrl mode will listen for (D731).
  var MODIFIERS = {
    Control: true,
    Shift: true,
    Alt: true,
    AltGraph: true,
    Meta: true,
    OS: true,
    Fn: true,
    CapsLock: true,
    NumLock: true,
    ScrollLock: true,
  };
  // Content the owner left as it was (docs/tooltips-plan.md §5.4): emoji and
  // sticker cells, reaction chips, the feed's stickers and photographs, the
  // composer's attachment thumbnail, the stream picture. The audit passes over
  // them, `title` and all.
  var CONTENT = [
    ".emoji-panel__glyph",
    ".quick-reactions__cell",
    ".reaction-chip",
    ".sticker-panel__cell",
    ".composer__stickers-cell",
    ".message-sticker",
    ".media-album__cell",
    ".media-album__play",
    ".attachment-chip__open",
    ".stream-view__frame",
    // The web's album draws its picture by hand rather than in the desktop's
    // cells (clients/web/src/react/components/attachments.tsx).
    ".media-album__slide",
  ].join(",");
  // Where a link goes, which its words need not say — a text link's address, a
  // link card's page — keeps the browser's `title` until the owner decides
  // (docs/tooltips-plan.md §12): the audit passes over it too.
  var DESTINATIONS = "a[href], .link-card__main";

  var anchor = null; // the control whose tip is up, or on its way
  var byHover = false; // what holds it: the pointer resting on it…
  var byFocus = false; // …and the keyboard's focus on it
  var shown = false; // drawn, rather than waited for
  var words = ""; // what the drawn tip says
  var box = null; // where its control stood when it was placed
  var timer = 0;
  var frame = 0;
  var warmUntil = 0;
  // A control just pressed: its tip stays down until the pointer has left it,
  // the way a native tooltip does after a click.
  var pressed = null;

  function now() {
    return Date.now();
  }

  // The control a target belongs to, if it has a tip to give.
  function anchorOf(target) {
    if (!(target instanceof Element)) return null;
    if (target.closest(NONE) !== null) return null;
    return target.closest(TIP);
  }

  // What a control's tip says now — or nothing, while the words the layout may
  // hide are on screen.
  function wordsOf(control) {
    var said = (control.getAttribute("data-tip") || "").trim();
    if (!said) return "";
    var label = control.querySelector(LABEL);
    if (label !== null && label.getClientRects().length > 0) return "";
    return said;
  }

  // This window's pool: the mount's own `.echo-tip` children, and not the box
  // the Ctrl mode draws into beside them.
  function pool(mount) {
    return mount.querySelectorAll(":scope > .echo-tip");
  }

  // The i-th tip of this window's pool, made when first asked for.
  function tip(i) {
    var mount = document.querySelector(MOUNT);
    if (mount === null) return null;
    var tips = pool(mount);
    for (var n = tips.length; n <= i; n++) {
      var node = document.createElement("div");
      node.className = "echo-tip";
      node.setAttribute("aria-hidden", "true");
      var label = document.createElement("span");
      label.className = "echo-tip__label";
      node.appendChild(label);
      mount.appendChild(node);
    }
    return pool(mount)[i];
  }

  // «Ctrl+Shift+M» — or «Ctrl + Shift + M», as the desktop's settings write a
  // chord — as one keycap a key, after whatever el holds (D742). A key named
  // «+» is the one case the split cannot read, and no control has it.
  function keycaps(el, keys) {
    var box = document.createElement("span");
    box.className = "echo-tip__keys";
    var parts = String(keys).split("+");
    for (var i = 0; i < parts.length; i++) {
      var part = parts[i].trim();
      if (!part) continue;
      var cap = document.createElement("span");
      cap.className = "echo-keycap";
      cap.textContent = part;
      box.appendChild(cap);
    }
    el.appendChild(box);
    return box;
  }

  // The tip's words, and the control's keys after them when it has any: its
  // own, or the chord of the static map that presses it (D747).
  function fill(node, said, control) {
    node.firstChild.textContent = said;
    while (node.childNodes.length > 1) node.removeChild(node.lastChild);
    var keys = (control.getAttribute("data-tip-keys") || "").trim();
    var mark = control.getAttribute("data-hotkey");
    if (!keys && mark && typeof window.__echoKeytips === "object" && window.__echoKeytips !== null) {
      keys = window.__echoKeytips.chord(mark);
    }
    if (keys) keycaps(node, keys);
  }

  // The Ctrl mode is up: its own tips say everything this one would.
  function moded() {
    return typeof window.__echoKeytips === "object" && window.__echoKeytips !== null && window.__echoKeytips.up();
  }

  function place(node, control) {
    var r = control.getBoundingClientRect();
    var size = { w: node.offsetWidth, h: node.offsetHeight };
    var bounds = { w: window.innerWidth, h: window.innerHeight };
    var p;
    if (typeof window.__echoPlace === "function") {
      p = window.__echoPlace({ x: r.left, y: r.top, w: r.width, h: r.height }, size, bounds, {
        side: control.getAttribute("data-tip-side") || "top",
        align: "center",
        offset: GAP,
        edge: EDGE,
      });
    } else {
      // Only if `placement.js` never loaded: above and centred, kept inside.
      var x = r.left + r.width / 2 - size.w / 2;
      var y = r.top - GAP - size.h < EDGE ? r.bottom + GAP : r.top - GAP - size.h;
      p = {
        x: Math.round(Math.max(EDGE, Math.min(x, bounds.w - EDGE - size.w))),
        y: Math.round(y),
      };
    }
    node.style.left = p.x + "px";
    node.style.top = p.y + "px";
    box = { x: r.left, y: r.top, w: r.width, h: r.height };
  }

  function reveal() {
    timer = 0;
    if (anchor === null) return;
    var said = moded() ? "" : wordsOf(anchor);
    var node = said ? tip(0) : null;
    if (node === null) {
      hide();
      return;
    }
    words = said;
    fill(node, said, anchor);
    node.classList.remove("echo-tip--shown");
    node.classList.add("echo-tip--up");
    // Laid out and measured at opacity 0, then placed (D425's «do the trying
    // at opacity 0»); the frame it stood invisible in is where the fade starts.
    place(node, anchor);
    void node.offsetWidth;
    node.classList.add("echo-tip--shown");
    shown = true;
    if (!frame) frame = window.requestAnimationFrame(tick);
  }

  function hide() {
    if (timer) {
      window.clearTimeout(timer);
      timer = 0;
    }
    if (frame) {
      window.cancelAnimationFrame(frame);
      frame = 0;
    }
    if (shown) warmUntil = now() + WARM;
    var mount = document.querySelector(MOUNT);
    var node = mount === null ? null : pool(mount)[0];
    if (node) node.classList.remove("echo-tip--up", "echo-tip--shown");
    anchor = null;
    byHover = false;
    byFocus = false;
    shown = false;
    words = "";
    box = null;
  }

  // Once a frame while a tip is up: its control may have moved (a list laid
  // out again under it), changed its words (a mute toggled by its chord), or
  // gone — and no event would say so.
  function tick() {
    frame = 0;
    if (anchor === null || !shown) return;
    var said = anchor.isConnected ? wordsOf(anchor) : "";
    var r = said ? anchor.getBoundingClientRect() : null;
    var node = r !== null && (r.width > 0 || r.height > 0) ? tip(0) : null;
    if (node === null) {
      hide();
      return;
    }
    if (said !== words) {
      words = said;
      fill(node, said, anchor);
      box = null;
    }
    if (box === null || r.left !== box.x || r.top !== box.y || r.width !== box.w || r.height !== box.h) {
      place(node, anchor);
    }
    frame = window.requestAnimationFrame(tick);
  }

  // One tip at a time, the newest trigger winning.
  function begin(control, viaFocus) {
    if (control !== anchor) hide();
    anchor = control;
    if (viaFocus) byFocus = true;
    else byHover = true;
    if (shown) return;
    if (viaFocus || now() < warmUntil) {
      if (timer) {
        window.clearTimeout(timer);
        timer = 0;
      }
      reveal();
    } else if (!timer) {
      timer = window.setTimeout(reveal, DELAY);
    }
  }

  // The arrows' item (`menukeys.js`, D763): its tip at once, as the keyboard's
  // focus raises one, though the focus stays where it was. It goes the way a
  // focus's tip goes, and with the next key the arrows take.
  function show(el) {
    if (el instanceof Element && anchorOf(el) === el) begin(el, true);
  }

  // A tip held by both the pointer and the focus goes when the last lets go.
  function letGo(viaFocus) {
    if (viaFocus) byFocus = false;
    else byHover = false;
    if (!byHover && !byFocus) hide();
  }

  document.addEventListener(
    "pointerover",
    function (e) {
      if (e.pointerType !== "mouse" && e.pointerType !== "pen") return;
      var control = anchorOf(e.target);
      if (pressed !== null && control !== pressed) pressed = null;
      if (control === null) {
        if (anchor !== null && byHover) letGo(false);
        return;
      }
      if (control === pressed) return;
      if (control === anchor) {
        byHover = true;
        return;
      }
      begin(control, false);
    },
    true,
  );

  // Off the window altogether: no `pointerover` follows that one.
  document.addEventListener(
    "pointerout",
    function (e) {
      if (e.relatedTarget !== null) return;
      pressed = null;
      if (anchor !== null && byHover) letGo(false);
    },
    true,
  );

  document.addEventListener(
    "focusin",
    function (e) {
      var control = anchorOf(e.target);
      if (control === null) return;
      var keyboard = false;
      try {
        keyboard = e.target.matches(":focus-visible");
      } catch (err) {
        keyboard = false;
      }
      if (keyboard) begin(control, true);
    },
    true,
  );

  document.addEventListener(
    "focusout",
    function (e) {
      if (anchor === null || !byFocus) return;
      if (anchorOf(e.target) === anchor) letGo(true);
    },
    true,
  );

  document.addEventListener(
    "pointerdown",
    function (e) {
      pressed = anchorOf(e.target);
      hide();
    },
    true,
  );

  // Enter and Space included, so a tip is never left beside the menu its
  // control has just opened. Never prevented, never stopped (D726).
  document.addEventListener(
    "keydown",
    function (e) {
      if (MODIFIERS[e.key] === true) return;
      hide();
    },
    true,
  );

  document.addEventListener(
    "wheel",
    function () {
      if (anchor !== null) hide();
    },
    { capture: true, passive: true },
  );

  // A scroll of whatever holds the control would leave the tip standing where
  // the control was. A scroll anywhere else — the feed taking a new message in
  // while the pointer rests on the header — leaves it be.
  document.addEventListener(
    "scroll",
    function (e) {
      if (anchor === null) return;
      var t = e.target;
      if (t === document || t === document.documentElement || (t instanceof Node && t.contains(anchor))) hide();
    },
    true,
  );

  window.addEventListener("blur", function () {
    if (anchor !== null) hide();
  });
  document.addEventListener("visibilitychange", function () {
    if (document.hidden && anchor !== null) hide();
  });

  // ── The audit ──
  //
  // What the migration has to leave at nothing, run by hand in every window
  // (docs/tooltips-plan.md §9, step 5). It sees what the markup says: a `div`
  // whose click handler a framework attached and that has no role is
  // invisible to it — give it a role.

  var CONTROLS =
    "button, a[href], input:not([type=hidden]), select, textarea, " +
    "[role=button], [role=switch], [role=checkbox], [role=radio], [role=tab], " +
    "[role=slider], [role=menuitem], [role=option], [tabindex]:not([tabindex='-1'])";
  // Fields have a placeholder or a label of their own, and no tip.
  var FIELDS =
    "input:not([type]), input[type=text], input[type=search], input[type=email], " +
    "input[type=password], input[type=number], input[type=url], input[type=tel], " +
    "textarea, select, [contenteditable=''], [contenteditable=true]";

  // A letter makes words; a number, a time, a count or an emoji standing as an
  // icon does not (D722). An input's `<label>` is its words: a switch or a
  // slider in a row whose title is that label is named by the row, and shows
  // no tip, as a `ds::Toggle` does (D730).
  function hasWords(el) {
    if (/\p{L}/u.test(el.innerText || "")) return true;
    var labels = el.tagName === "INPUT" ? el.labels : null;
    for (var i = 0; labels && i < labels.length; i++) {
      if (/\p{L}/u.test(labels[i].innerText || "")) return true;
    }
    return false;
  }

  function wordCount(el) {
    var said = (el.getAttribute("data-tip") || "").trim();
    return said ? said.split(/\s+/).length : 0;
  }

  function audit(root) {
    var scope = root || document;
    var out = [];
    var controls = scope.querySelectorAll(CONTROLS);
    for (var i = 0; i < controls.length; i++) {
      var el = controls[i];
      if (el.getClientRects().length === 0) continue;
      if (el.closest(CONTENT) !== null || el.closest(NONE) !== null || el.closest(MOUNT) !== null) continue;
      if (el.matches(FIELDS) || hasWords(el)) continue;
      var name = el.getAttribute("aria-label") || el.getAttribute("aria-labelledby");
      if (!name) out.push({ el: el, why: "no name" });
      // A switch is named by its row and shows no tip (D730).
      if (el.getAttribute("role") === "switch") continue;
      if (!el.hasAttribute("data-tip")) out.push({ el: el, why: "no tip" });
      else if (el.getAttribute("data-tip") !== el.getAttribute("aria-label"))
        out.push({ el: el, why: "tip is not the name" });
    }
    var titled = scope.querySelectorAll("[title]");
    for (var j = 0; j < titled.length; j++) {
      if (titled[j].closest(CONTENT) === null && !titled[j].matches(DESTINATIONS))
        out.push({ el: titled[j], why: "title" });
    }
    var tipped = scope.querySelectorAll(TIP);
    for (var k = 0; k < tipped.length; k++) {
      var t = tipped[k];
      if (t.matches(CONTROLS) && !t.hasAttribute("data-tip-name") && !t.hasAttribute("data-tip-sentence") && wordCount(t) > 2)
        out.push({ el: t, why: "past two words" });
    }
    return out;
  }

  window.__echoTips = { hide: hide, show: show, audit: audit, words: wordsOf, keycaps: keycaps };
})();
